<template>
  <nav class="learning-spoke-nav" aria-label="Learning">
    <div class="spoke-inner">
      <router-link
        v-for="item in items"
        :key="item.key"
        :to="item.to"
        class="spoke-link"
        :class="{ active: isActive(item) }"
      >
        {{ item.label }}
      </router-link>
    </div>
  </nav>
</template>

<script>
/**
 * Learning spoke local nav (hub-and-spoke).
 * Sparse Learning landmark lands on catalog; Admin / Engagement stay reachable here.
 * @see docs/product/HUB_SPOKE_PORTAL.md
 */
import auth from '@/services/auth';
import api from '@/services/api';

export default {
  name: 'LearningSpokeNav',
  data() {
    return {
      vetClinicalProjectId: null,
    };
  },
  computed: {
    path() {
      return this.$route.path;
    },
    queryTab() {
      return this.$route.query?.tab || 'browse';
    },
    canManageLms() {
      return auth.isLmsAdmin();
    },
    consultSpokeTo() {
      const query = { tab: 'agent' };
      if (this.$route.query.compliance_subject_id) {
        query.compliance_subject_id = this.$route.query.compliance_subject_id;
      }
      return { path: '/learning', query };
    },
    items() {
      const items = [
        {
          key: 'catalog',
          to: { path: '/learning', query: { tab: 'browse' } },
          label: 'Catalog',
          match: () => this.path === '/learning' || this.path === '/learning/'
            ? this.queryTab !== 'agent'
            : this.path.startsWith('/learning/search') || this.path.startsWith('/learning/courses'),
        },
        {
          key: 'my-courses',
          to: '/learning/my-courses',
          label: 'My courses',
          match: () => this.path.startsWith('/learning/my-courses'),
        },
        {
          key: 'library',
          to: '/learning/my-library',
          label: 'Library',
          match: () => this.path.startsWith('/learning/my-library'),
        },
        {
          key: 'agent',
          to: this.consultSpokeTo,
          label: 'Vet Consult',
          match: () => (this.path === '/learning' || this.path === '/learning/') && this.queryTab === 'agent',
        },
      ];

      if (this.vetClinicalProjectId) {
        items.push({
          key: 'patients',
          to: '/learning/patients',
          label: 'Patients',
          match: () => this.path.startsWith('/learning/patients'),
        });
      }

      if (this.canManageLms) {
        items.push(
          {
            key: 'admin',
            to: '/learning/admin',
            label: 'Manage courses',
            match: () => this.path.startsWith('/learning/admin'),
          },
          {
            key: 'engagement',
            to: '/learning/engagement',
            label: 'Engagement',
            match: () => this.path.startsWith('/learning/engagement'),
          },
        );
      }
      return items;
    },
  },
  async mounted() {
    await this.loadVetClinicalProject();
  },
  methods: {
    isActive(item) {
      return item.match();
    },
    async loadVetClinicalProject() {
      try {
        const response = await api.get('/projects/');
        const payload = response.data;
        const projects = Array.isArray(payload)
          ? payload
          : (payload?.projects || payload?.results || []);
        const match = projects.find((project) => (
          project.project_type_code === 'VET_CLINICAL'
          || project.project_type === 'VET_CLINICAL'
        ));
        this.vetClinicalProjectId = match?.id || null;
      } catch {
        this.vetClinicalProjectId = null;
      }
    },
  },
};
</script>

<style scoped>
.learning-spoke-nav {
  background: var(--p-surface-elevated, #fff);
  border-bottom: 1px solid var(--p-border, #e2e8f0);
  margin: calc(-1 * var(--spacing-lg, 1.5rem)) calc(-1 * var(--spacing-lg, 1.5rem)) var(--spacing-lg, 1.5rem);
  padding: 0.5rem var(--spacing-lg, 1.5rem);
  position: sticky;
  top: 3.5rem;
  z-index: 40;
}

.spoke-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.spoke-link {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.75rem;
  border-radius: 0.5rem;
  color: var(--p-ink-muted, #64748b);
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: none;
}

.spoke-link:hover {
  background: var(--p-surface-muted, #f1f5f9);
  color: var(--p-ink, #1e293b);
}

.spoke-link.active {
  background: var(--p-accent-subtle, #eef2f8);
  color: var(--p-accent, #1e3a5f);
}
</style>
